import { type ReactNode, useState, useEffect } from 'react'
import { ArrowDecreaseIcon, ArrowIncreaseIcon } from './Icons'
import { SectionCardsSkeleton } from './TableSkeletons'
import { cn } from '@/lib/utils'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { months } from '@/constants'

interface SectionCardProps {
  title: string
  value: string | number
  icon?: ReactNode
  cardType?: 1 | 2 | 3 | 4
  growth?: {
    value: string
    isPositive: boolean
  }
  trailtext?: string
  footer?: ReactNode
}

export interface DateFilterValues {
  month: string
  year: string
}

interface SectionCardsProps {
  cards: SectionCardProps[]
  isLoading?: boolean
  className?: string
  cardLabel?: string
  showFilters?: boolean
  filterValues?: DateFilterValues
  cardLabelClassName?: string
  onFilterChange?: (filters: DateFilterValues) => void
}

export function SectionCard({
  title,
  value,
  icon,
  cardType = 1,
  growth,
  trailtext,
  footer,
}: Readonly<SectionCardProps>) {
  const getCardClasses = (type: number) => {
    const baseClasses =
      'w-10 h-10 rounded-full flex items-center justify-center shadow-lg'
    const shadowStyle =
      '0px 10px 17px 0px rgba(77, 42, 250, 0.2), inset 0px 0px 4px 2px rgba(0, 0, 0, 0.16), inset 0px 5px 6px 0px rgba(255, 255, 255, 0.14)'

    switch (type) {
      case 1:
        return {
          classes: `${baseClasses} bg-[image:var(--gradient-ss-card-1)]`,
          style: { boxShadow: shadowStyle },
        }
      case 2:
        return {
          classes: `${baseClasses} bg-[image:var(--gradient-ss-card-2)]`,
          style: { boxShadow: shadowStyle },
        }
      case 3:
        return {
          classes: `${baseClasses} bg-[image:var(--gradient-ss-card-3)]`,
          style: { boxShadow: shadowStyle },
        }
      case 4:
        return {
          classes: `${baseClasses} bg-[image:var(--gradient-ss-card-4)]`,
          style: { boxShadow: shadowStyle },
        }
      default:
        return {
          classes: `${baseClasses} bg-[image:var(--gradient-ss-card-1)]`,
          style: { boxShadow: shadowStyle },
        }
    }
  }

  const getBackgroundImage = (type: number) => {
    switch (type) {
      case 1:
        return 'var(--bg-ss-card-1)'
      case 2:
        return 'var(--bg-ss-card-2)'
      case 3:
        return 'var(--bg-ss-card-3)'
      case 4:
        return 'var(--bg-ss-card-4)'
      default:
        return 'var(--bg-ss-card-1)'
    }
  }

  const cardStyle = getCardClasses(cardType)

  return (
    <section
      className="relative w-full min-w-0 h-[110px] rounded-xl border border-ss-neutral-50 bg-ss-neutral-white overflow-hidden p-2.5 pb-0 border-l-none"
      style={{
        background: `var(--color-ss-neutral-white) ${getBackgroundImage(cardType)} no-repeat right -4px bottom/cover`,
      }}
      aria-label={`Summary card: ${title}`}
    >
      {/* Glassmorphism Card Content */}
      <div className="rounded-t-xl rounded-tr-2xl border border-white/40 backdrop-blur-sm bg-white/40">
        <div className="flex flex-col gap-2.5 h-full p-1.5 pr-4">
          {/* Header with Icon and Title */}
          <div className="flex items-center gap-3 flex-1">
            {icon && (
              <div className={cardStyle.classes} style={cardStyle.style}>
                <div
                  className="text-ss-neutral-white text-lg"
                  aria-hidden="true"
                >
                  {icon}
                </div>
              </div>
            )}
            <h3 className="text-base font-semibold text-ss-neutral-950 leading-[22px]">
              {title}
            </h3>
          </div>

          {/* Value and Footer/Growth */}
          <div className="flex items-center justify-between">
            <span className="text-2xl font-bold text-ss-neutral-950 leading-8 tabular-nums">
              {value}
            </span>
            {typeof footer !== 'undefined' ? (
              <div className="text-xs text-ss-neutral-600 font-medium text-right">
                {footer}
              </div>
            ) : growth ? (
              <div className="flex items-center gap-1">
                {growth.isPositive ? (
                  <span className="text-ss-positive-200 flex items-center gap-1 text-xs font-medium">
                    <ArrowIncreaseIcon aria-hidden="true" />
                    <span className="sr-only">Increase</span>
                    {growth.value}
                  </span>
                ) : (
                  <span className="text-ss-critical-200 flex items-center gap-1 text-xs font-medium">
                    <ArrowDecreaseIcon aria-hidden="true" />
                    <span className="sr-only">Decrease</span>
                    {growth.value}
                  </span>
                )}{' '}
                <span className="text-xs text-ss-neutral-600 font-medium">
                  {' '}
                  last month
                </span>
              </div>
            ) : trailtext ? (
              <span className="text-xs text-ss-neutral-600 font-medium">
                {trailtext}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  )
}

// Date Filter Component
function DateFilters({
  filterValues,
  onFilterChange,
}: Readonly<{
  filterValues?: DateFilterValues
  onFilterChange?: (filters: DateFilterValues) => void
}>) {
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth() + 1

  const [selectedMonth, setSelectedMonth] = useState<string>(
    filterValues?.month ?? currentMonth.toString()
  )
  const [selectedYear, setSelectedYear] = useState<string>(
    filterValues?.year ?? currentYear.toString()
  )

  const years = Array.from({ length: 6 }, (_, i) => currentYear - i)

  const getAvailableMonths = () => {
    if (selectedYear === currentYear.toString()) {
      return months.filter(
        month => Number.parseInt(month.value) <= currentMonth
      )
    }
    return months
  }

  const handleYearChange = (year: string) => {
    setSelectedYear(year)
    if (
      year === currentYear.toString() &&
      selectedMonth &&
      Number.parseInt(selectedMonth) > currentMonth
    ) {
      setSelectedMonth(currentMonth.toString())
      onFilterChange?.({ month: currentMonth.toString(), year })
    } else {
      onFilterChange?.({ month: selectedMonth, year })
    }
  }

  const handleMonthChange = (month: string) => {
    setSelectedMonth(month)
    onFilterChange?.({ month, year: selectedYear })
  }

  useEffect(() => {
    onFilterChange?.({
      month: filterValues?.month ?? currentMonth.toString(),
      year: filterValues?.year ?? currentYear.toString(),
    })
  }, [])

  useEffect(() => {
    if (
      selectedYear === currentYear.toString() &&
      selectedMonth &&
      Number.parseInt(selectedMonth) > currentMonth
    ) {
      setSelectedMonth(currentMonth.toString())
    }
  }, [selectedYear, selectedMonth, currentMonth, currentYear])

  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <Select value={selectedYear} onValueChange={handleYearChange}>
          <SelectTrigger
            className={cn(
              'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20'
            )}
          >
            <SelectValue placeholder="Select Year" />
          </SelectTrigger>
          <SelectContent>
            {years.map(year => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="relative ">
        <Select value={selectedMonth} onValueChange={handleMonthChange}>
          <SelectTrigger
            className={cn(
              'h-11 border-ss-neutral-100 bg-ss-neutral-white focus:border-ss-blue-500 focus:ring-ss-blue-500/20'
            )}
          >
            <SelectValue placeholder="Select Month" />
          </SelectTrigger>
          <SelectContent>
            {getAvailableMonths().map(month => (
              <SelectItem key={month.value} value={month.value}>
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

export function SectionCards({
  cards,
  isLoading = false,
  className,
  cardLabel,
  showFilters = false,
  filterValues,
  cardLabelClassName,
  onFilterChange,
}: Readonly<SectionCardsProps>) {
  if (isLoading) {
    return <SectionCardsSkeleton cardCount={cards.length || 4} />
  }

  return (
    <>
      {(cardLabel || showFilters) && (
        <div
          className={cn(
            'flex items-center justify-between px-4 lg:px-7 mb-2',
            cardLabelClassName
          )}
        >
          {cardLabel && (
            <h2 className="text-xl font-bold text-ss-neutral-950 ">
              {cardLabel}
            </h2>
          )}

          {showFilters && (
            <div className="ml-auto">
              <DateFilters
                filterValues={filterValues}
                onFilterChange={onFilterChange}
              />
            </div>
          )}
        </div>
      )}

      {/* Cards Grid */}
      <div
        className={cn(
          'grid grid-cols-1 gap-4 px-4 lg:px-6 md:grid-cols-2 xl:grid-cols-4',
          className
        )}
        aria-label="Summary cards grid"
      >
        {cards.map((card, index) => (
          <SectionCard key={index} {...card} />
        ))}
      </div>
    </>
  )
}
